import CloseIcon from '@mui/icons-material/Close';
import { DialogContent, Grid, Stack, Button } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { IStageStatusPair } from 'wisestep-api';
import {
  WsDialog,
  WsDialogSize,
  WsButton,
  WsTypography,
  WsTypoVariants,
  WsColors,
} from 'wisestep-base';

import {
  stageStatusPipelineType,
  statusesType,
} from '../../../../../features/crmSettings/customizeStageStatus/helper';
import ErrorLogger from '../../../../../providers/ErrorLogger';
import {
  getCandidateStageData,
  getStageStatusSettings,
} from '../../../../../utils/CandidateStageStatusHelper';

interface StageStatusFilterPopUpProps {
  open: boolean;
  onClose: () => void;
  onSelect: (data: IStageStatusPair[]) => void;
  stageStatus?: IStageStatusPair[];
}

const StageStatusFilterPopUp = ({
  open,
  onClose,
  onSelect,
  stageStatus,
}: StageStatusFilterPopUpProps) => {
  const [stagesData, setStagesData] = useState<stageStatusPipelineType[]>([]);
  const [selectedStageStatuses, setSelectedStageStatuses] = useState<IStageStatusPair[]>(
    stageStatus ?? []
  );

  useEffect(() => {
    (async () => {
      const response = await getStageStatusSettings();
      setStagesData(getCandidateStageData(response?.data));
    })();
  }, []);

  const isStageStatusSelected = (subStage: string, stage: string) => {
    let isExists = false;
    for (const selectedStageStatus of selectedStageStatuses) {
      if (
        selectedStageStatus.applicationStage == stage &&
        selectedStageStatus.applicationState == subStage
      ) {
        isExists = true;
        break;
      }
    }
    return isExists;
  };

  const addSelectedState = (subStage: string, stage: string) => {
    setSelectedStageStatuses([
      ...selectedStageStatuses,
      {
        applicationStage: stage,
        applicationState: subStage,
      },
    ]);
  };

  const removeSelectedState = (subStage: string, stage: string) => {
    setSelectedStageStatuses((prevSelectedStageStatuses) => {
      return prevSelectedStageStatuses.filter((item) => {
        return !(item.applicationStage === stage && item.applicationState === subStage);
      });
    });
  };

  const alterSelectedState = (subStage: string, stage: string) => {
    const isExists = isStageStatusSelected(subStage, stage);
    if (!isExists) {
      addSelectedState(subStage, stage);
    } else removeSelectedState(subStage, stage);
  };

  const removeAllSubStagesOfStage = (stage: string) => {
    let allStatusArray = selectedStageStatuses;
    allStatusArray = allStatusArray.filter(
      (stageStatus) => !(stageStatus.applicationStage == stage)
    );
    setSelectedStageStatuses(allStatusArray);
  };

  const addAllSubStagesOfStage = (stage: string, status: statusesType[]) => {
    let allStatusArray: IStageStatusPair[] = [];
    if (status?.length > 0) {
      status.forEach((subStage) => {
        if (!isStageStatusSelected(subStage.value, stage)) {
          allStatusArray = [
            ...allStatusArray,
            {
              applicationStage: stage,
              applicationState: subStage.value,
            },
          ];
        }
      });
    }
    setSelectedStageStatuses([...selectedStageStatuses, ...allStatusArray]);
  };

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <WsDialog openDialog={open} size={WsDialogSize.LARGE}>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Stack direction="row" spacing={1}>
            {'Stage Status Filter'}
            {onClose ? (
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Grid container padding={1}>
            <Stack direction="column" spacing={2}>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                {stagesData?.map((stage, index) => (
                  <Grid item key={index}>
                    <Stack direction="column" spacing={1}>
                      <Stack direction="row" spacing={2}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE} cursor="pointer">
                          {String(stage.stage)} {stage.isArchive && '(Archived)'}
                        </WsTypography>
                        <Stack direction="row">
                          <WsButton
                            variant="outlined"
                            size="small"
                            onClick={() => addAllSubStagesOfStage(stage.stage, stage.statuses)}
                          >
                            {'Select All'}
                          </WsButton>
                          <WsButton
                            variant="outlined"
                            size="small"
                            onClick={() => removeAllSubStagesOfStage(stage.stage)}
                          >
                            {'Unselect All'}
                          </WsButton>
                        </Stack>
                      </Stack>

                      <Grid direction="row" container spacing={1}>
                        {stage.statuses.map((subStage, subIndex) => (
                          <Grid key={subIndex} item>
                            {subStage.isArchive ? (
                              <Button
                                variant={
                                  isStageStatusSelected(subStage.value, stage.stage)
                                    ? 'contained'
                                    : 'outlined'
                                }
                                color={'secondary'}
                                sx={{ color: WsColors.GreyBlack }}
                                onClick={() => alterSelectedState(subStage.value, stage.stage)}
                              >
                                <>{subStage.value} (Archived)</>
                              </Button>
                            ) : (
                              <WsButton
                                variant={
                                  isStageStatusSelected(subStage.value, stage.stage)
                                    ? 'contained'
                                    : 'outlined'
                                }
                                onClick={() => alterSelectedState(subStage.value, stage.stage)}
                              >
                                {subStage.value}
                              </WsButton>
                            )}
                          </Grid>
                        ))}
                      </Grid>
                    </Stack>
                  </Grid>
                ))}
              </ErrorBoundary>
            </Stack>
          </Grid>
          <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
            <WsButton variant="outlined" onClick={onClose}>
              Cancel
            </WsButton>
            <WsButton variant="contained" onClick={() => onSelect(selectedStageStatuses)}>
              Continue
            </WsButton>
          </Stack>
        </DialogContent>
      </WsDialog>
    </ErrorBoundary>
  );
};

export default StageStatusFilterPopUp;
